import React, { useRef } from "react";
import { motion, useTransform, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Footer from "../components/Footer";
import { RAMSEY_ENABLED } from "../config/features";
import { ChapterCard, CodeReveal, NarrativeCaption } from "../components/story";
import { SystemsCapabilities } from "../components/story/SystemsCapabilities";
import { useScrollContext } from "../context/ScrollContext";

const Section = ({ children, id, className = "", "aria-labelledby": ariaLabelledby }) => {
  const prefersReduced = useReducedMotion();
  const transition = prefersReduced
    ? { opacity: { duration: 0.3 } }
    : { duration: 0.8, ease: [0.16, 1, 0.3, 1] };

  return (
    <motion.section
      id={id}
      aria-labelledby={ariaLabelledby}
      initial={prefersReduced ? { opacity: 0 } : { opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: prefersReduced ? 0 : 30 }}
      viewport={{ once: true }}
      transition={transition}
      className={`py-24 px-6 ${className}`}
    >
      {children}
    </motion.section>
  );
};

const OrbitalHero = () => {
  const prefersReduced = useReducedMotion();
  const { scrollYProgressSpring } = useScrollContext();
  const ref = useRef(null);

  const ringRotation = useTransform(scrollYProgressSpring, [0, 1], ["0deg", "360deg"]);
  const innerRingRotation = useTransform(scrollYProgressSpring, [0, 1], ["0deg", "-180deg"]);

  if (prefersReduced) {
    return (
      <div
        ref={ref}
        className="absolute inset-0 -z-10 pointer-events-none overflow-hidden"
        style={{ maskImage: "linear-gradient(to bottom, black 60%, transparent 100%)" }}
        aria-hidden="true"
      >
        {/* Static orbs for reduced motion */}
        <div
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{
            background: "radial-gradient(circle at 30% 30%, var(--ice-primary) 0%, transparent 70%)",
            filter: "blur(128px)"
          }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-72 h-72 rounded-full opacity-15 blur-3xl"
          style={{
            background: "radial-gradient(circle at 70% 70%, var(--ice-deep) 0%, transparent 70%)",
            filter: "blur(128px)"
          }}
        />
        <div
          className="absolute top-1/2 left-1/2 w-48 h-48 rounded-full opacity-25 blur-3xl"
          style={{
            background: "radial-gradient(circle at 50% 50%, var(--ice-accent) 0%, transparent 70%)",
            filter: "blur(96px)"
          }}
        />
        {/* Static tensor rings */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-white/5 opacity-30 pointer-events-none"
          style={{
            borderWidth: "2px",
            borderStyle: "solid",
            borderImage: "linear-gradient(45deg, var(--ice-primary), var(--ice-deep), transparent, var(--ice-primary)) 1",
            boxShadow: "0 0 80px rgba(111, 182, 232, 0.1), inset 0 0 80px rgba(111, 182, 232, 0.05)"
          }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-white/5 opacity-20 pointer-events-none"
          style={{
            borderWidth: "1px",
            borderStyle: "dashed",
            borderImage: "linear-gradient(45deg, transparent, var(--ice-accent), transparent) 1"
          }}
        />
      </div>
    );
  }

  return (
    <motion.div
      ref={ref}
      className="absolute inset-0 -z-10 pointer-events-none overflow-hidden"
      style={{ maskImage: "linear-gradient(to bottom, black 60%, transparent 100%)" }}
      aria-hidden="true"
    >
      {/* Orb 1 - Large cyan orb */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-20 blur-3xl"
        style={{
          background: "radial-gradient(circle at 30% 30%, var(--ice-primary) 0%, transparent 70%)",
          filter: "blur(128px)"
        }}
        animate={{
          scale: [1, 1.15, 1],
          translateX: ["0%", "8%", "0%"],
          translateY: ["0%", "-5%", "0%"]
        }}
        transition={{ duration: 20, ease: "easeInOut", repeat: Infinity }}
      />

      {/* Orb 2 - Medium blue orb */}
      <motion.div
        className="absolute bottom-1/4 right-1/4 w-72 h-72 rounded-full opacity-15 blur-3xl"
        style={{
          background: "radial-gradient(circle at 70% 70%, var(--ice-deep) 0%, transparent 70%)",
          filter: "blur(128px)"
        }}
        animate={{
          scale: [1, 1.2, 1],
          translateX: ["0%", "-6%", "0%"],
          translateY: ["0%", "4%", "0%"]
        }}
        transition={{ duration: 25, ease: "easeInOut", repeat: Infinity, repeatDelay: 2 }}
      />

      {/* Orb 3 - Small accent orb */}
      <motion.div
        className="absolute top-1/2 left-1/2 w-48 h-48 rounded-full opacity-25 blur-3xl"
        style={{
          background: "radial-gradient(circle at 50% 50%, var(--ice-accent) 0%, transparent 70%)",
          filter: "blur(96px)"
        }}
        animate={{
          scale: [1, 1.1, 1],
          translateX: ["0%", "4%", "0%"],
          translateY: ["0%", "-3%", "0%"]
        }}
        transition={{ duration: 18, ease: "easeInOut", repeat: Infinity, repeatDelay: 1 }}
      />

      {/* Tensor Ring - Scroll-linked rotation */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-white/5 opacity-30 pointer-events-none"
        style={{
          borderWidth: "2px",
          borderStyle: "solid",
          borderImage: "linear-gradient(45deg, var(--ice-primary), var(--ice-deep), transparent, var(--ice-primary)) 1",
          boxShadow: "0 0 80px rgba(111, 182, 232, 0.1), inset 0 0 80px rgba(111, 182, 232, 0.05)",
          rotate: ringRotation
        }}
      />

      {/* Inner tensor ring */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-white/5 opacity-20 pointer-events-none"
        style={{
          borderWidth: "1px",
          borderStyle: "dashed",
          borderImage: "linear-gradient(45deg, transparent, var(--ice-accent), transparent) 1",
          rotate: innerRingRotation
        }}
      />
    </motion.div>
  );
};

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "name": "Dopove",
      "url": "https://dopove.com",
      "logo": "https://dopove.com/logo_new_16x9_trans_inverted.png",
      "sameAs": [
        "https://x.com/dopove",
        "https://peerlist.io/dopove",
        "https://github.com/dopove",
        "https://linkedin.com/company/dopove"
      ],
      "foundingDate": "2024",
      "description": "Dopove builds useful memory for AI agents and applies systems thinking to how it works. Its first product is ICE, the Indexless Context Engine."
    },
    {
      "@type": "WebSite",
      "url": "https://dopove.com",
      "name": "Dopove",
      "description": "Dopove builds useful memory for AI agents and applies systems thinking to how it works. Its first product is ICE, the Indexless Context Engine.",
      "publisher": {
        "@type": "Organization",
        "name": "Dopove"
      }
    },
    {
      "@type": "ItemList",
      "name": "Dopove Products",
      "description": "Dopove products",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@type": "SoftwareApplication",
            "name": "ICE",
            "applicationCategory": "DeveloperApplication",
            "operatingSystem": "Cross-platform",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            },
            "url": "https://dopove.com/ice",
            "description": "Indexless Context Engine. Turn-granular memory for AI agents."
          }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@type": "SoftwareApplication",
            "name": "GoingMerry",
            "applicationCategory": "DeveloperApplication",
            "operatingSystem": "Cross-platform",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            },
            "url": "https://dopove.com/goingmerry",
            "description": "Local AI runtime. Run models privately on your own hardware."
          }
        },
        {
          "@type": "ListItem",
          "position": 3,
          "item": {
            "@type": "SoftwareApplication",
            "name": "Cortex",
            "applicationCategory": "DeveloperApplication",
            "operatingSystem": "Cross-platform",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            },
            "url": "https://github.com/Dopove/Cortex",
            "description": "Multi-agent orchestration runtime. Auditable, not just functional."
          }
        }
      ]
    }
  ]
};

export default function HomePage() {
  return (
    <div className="bg-[#191d2b] min-h-screen text-[#FFFFFF] flex flex-col font-sans selection:bg-[#6FB6E8]/30 overflow-x-hidden">
      <Helmet>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
        <title>Dopove: useful memory for AI agents</title>
        <meta name="description" content="Dopove builds useful memory for AI agents and applies systems thinking to how it works. Its first product is ICE, the Indexless Context Engine." />
        <meta property="og:title" content="Dopove: useful memory for AI agents" />
        <meta property="og:description" content="Dopove builds useful memory for AI agents and applies systems thinking to how it works. Its first product is ICE, the Indexless Context Engine." />
        <meta property="og:image" content="https://dopove.com/logo_new_16x9_trans_inverted.png" />
        <meta property="og:url" content="https://dopove.com" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Dopove: useful memory for AI agents" />
        <meta name="twitter:description" content="Dopove builds useful memory for AI agents and applies systems thinking to how it works. Its first product is ICE, the Indexless Context Engine." />
        <meta name="twitter:image" content="https://dopove.com/logo_new_16x9_trans_inverted.png" />
        <link rel="canonical" href="https://dopove.com" />
      </Helmet>

      
      <main className="flex-grow">
        <section
          id="hero"
          aria-labelledby="hero-heading"
          className="relative min-h-screen flex flex-col items-center justify-center px-6 pt-32 pb-20"
        >
          <OrbitalHero />

          <div className="max-w-6xl mx-auto w-full grid lg:grid-cols-2 gap-16 items-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="group relative inline-block mb-6">
                <span id="hero-badge" className="text-[#FFFFFF]/60 font-mono font-bold tracking-[0.4em] uppercase text-sm cursor-pointer relative z-10 transition-colors group-hover:text-[#6FB6E8] flex items-center gap-3">
                  Introducing ICE V3
                </span>
                {/* Animata-style floating blobs for hover charm */}
                <div className="absolute inset-0 pointer-events-none hover-lift focus-visible-ring transition-[opacity,transform] duration-500 ease-[var(--ease-out)] opacity-0 group-hover:opacity-100">
                  <div className="absolute -top-8 -left-4 w-4 h-4 bg-[#BFDFF6] rounded-full blur-xl animate-pulse-reduced" />
                  <div className="absolute -bottom-4 -right-8 w-6 h-6 bg-[#6FB6E8] rounded-full blur-xl animate-pulse-reduced" />
                  <div className="absolute top-2 -right-12 w-3 h-3 bg-[#3FA0E0] rounded-full blur-lg" />
                </div>
              </div>

              <h1 id="hero-heading" className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] text-balance text-[#FFFFFF] mb-6">
                Most agent memory forgets between conversations.{" "}
                <span className="block text-[#6FB6E8]">{"Ours doesn't."}</span>
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-[#FFFFFF]/70 font-light leading-relaxed max-w-lg mb-20">
                Built with systems thinking, in the product and in the company.
              </p>
              <div className="flex flex-col sm:flex-row items-center lg:items-start gap-4 mt-4">
                <a href="https://docs.dopove.com/docs/ice/quickstart" className="px-8 py-4 rounded-full bg-[#1E7FD1] text-white font-bold shadow-lg hover-scale-press focus-visible-ring transition-[transform,box-shadow] duration-200 ease-[var(--ease-out)]">
                  Get Started Free
                </a>
                <Link to="/ice" className="px-8 py-4 rounded-full border border-[#1E7FD1]/20 text-[#FFFFFF] font-bold hover:bg-[#6FB6E8]/5 hover-scale-press focus-visible-ring transition-[background-color,transform] duration-200 ease-[var(--ease-out)]">
                  Discover ICE V3
                </Link>
              </div>
            </motion.div>

            <div className="relative aspect-square w-full flex items-center justify-center lg:justify-end">
               <motion.div
                 initial={{ opacity: 0, scale: 0.9 }}
                 animate={{ opacity: 1, scale: 1 }}
                 transition={{ duration: 1, ease: "easeOut" }}
                 className="relative z-10 w-full max-w-lg aspect-square flex items-center justify-center bg-gradient-to-br from-[#BFDFF6]/5 to-[#6FB6E8]/10 rounded-[3rem] border border-[#FFFFFF]/10 shadow-2xl overflow-hidden backdrop-blur-sm"
               >
                 <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(111,182,232,0.1)_0%,transparent_50%)]" />
                 <img
                   src="/ICE_1x1_trans.png"
                   alt="ICE Logo"
                   className="w-full max-w-md h-auto object-contain relative z-10 drop-shadow-[0_0_50px_rgba(111,182,232,0.3)]"
                 />
               </motion.div>
            </div>
          </div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 0.5 }}
            className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-chevron-bounce"
            aria-hidden="true"
          >
            <svg className="w-6 h-6 text-[#FFFFFF]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </motion.div>
        </section>

        {/* The problem, stated before the product */}
        <section aria-labelledby="problem-heading" className="bg-[#191d2b] py-24 sm:py-32 border-y border-[#FFFFFF]/5">
          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-20">
            <h2 id="problem-heading" className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-balance text-[#FFFFFF]">
              Where stateful agents break down.
            </h2>
            <ul className="divide-y divide-[#FFFFFF]/10">
              <li className="py-6 text-xl sm:text-2xl font-light leading-snug text-[#FFFFFF]/85">Context that grows but does not change how the agent behaves.</li>
              <li className="py-6 text-xl sm:text-2xl font-light leading-snug text-[#FFFFFF]/85">Memory that is gone between conversations.</li>
              <li className="py-6 text-xl sm:text-2xl font-light leading-snug text-[#FFFFFF]/85">Retrieval that returns text that is semantically close, but wrong for the moment.</li>
            </ul>
          </div>
        </section>

        {/* Company, then product */}
        <Section className="max-w-6xl mx-auto">
          <p className="max-w-3xl text-2xl sm:text-3xl font-light leading-snug tracking-tight text-[#FFFFFF] mb-10">
            Dopove is a systems thinking company. We build useful memory for AI agents, and we apply the same thinking to how we run our own marketing.
          </p>
          <p className="max-w-3xl text-lg sm:text-xl font-light leading-relaxed text-[#FFFFFF]/70">
            ICE, the Indexless Context Engine, is turn-granular memory for AI agents that makes the context you already have useful.
          </p>
        </Section>

        {/* How ICE applies systems thinking */}
        <Section className="max-w-6xl mx-auto border-t border-[#FFFFFF]/5" aria-labelledby="ice-systems-heading">
          <SystemsCapabilities headingId="ice-systems-heading" />
        </Section>

        {/* The useful window */}
        <section className="bg-[#191d2b] py-24 sm:py-32 border-y border-[#FFFFFF]/5">
          <div className="max-w-4xl mx-auto px-6">
            <p className="text-3xl sm:text-4xl md:text-5xl font-light tracking-tight leading-[1.15] text-balance text-[#FFFFFF]">
              While others chase longer contexts, we ask what makes the information in the window useful. ICE works on the signal-to-noise ratio of the context an agent already has.
            </p>
          </div>
        </section>

        {/* SDK Preview / Developer First */}
        <Section className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[1fr_1.25fr] gap-12 lg:gap-16 items-center">
            <div>
              <span className="text-[#6FB6E8] font-mono font-bold tracking-[0.4em] uppercase text-xs mb-6 block">Developer First</span>
              <h2 className="text-4xl md:text-5xl font-extrabold mb-8 tracking-tight text-[#FFFFFF]">One import. <br />Persistent Memory.</h2>
              <p className="text-[#FFFFFF]/70 text-lg font-light leading-relaxed mb-8">
                ICE wraps standard LLM client libraries to automatically handle embedding, database storage, and context retrieval. No database setup, chunking logic, or index management needed.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4 text-[#FFFFFF]/80">
                  <div className="w-1.5 h-1.5 bg-[#6FB6E8] rounded-full" />
                  <p className="font-light">Transparent LLM Client Wrapper</p>
                </div>
                <div className="flex items-center gap-4 text-[#FFFFFF]/80">
                  <div className="w-1.5 h-1.5 bg-[#6FB6E8] rounded-full" />
                  <p className="font-light">Zero-Conf Local Storage</p>
                </div>
                <div className="flex items-center gap-4 text-[#FFFFFF]/80">
                  <div className="w-1.5 h-1.5 bg-[#6FB6E8] rounded-full" />
                  <p className="font-light">Multi-Platform Native Core C-Extension</p>
                </div>
              </div>
              <div className="mt-12">
                <a href="https://docs.dopove.com/docs/ice/api-reference" className="text-[#6FB6E8] font-bold hover:underline flex items-center gap-2 group">
                  View Full API Reference
                  <motion.span animate={{ transform: "translateX([0, 5, 0]px)" }} transition={{ repeat: Infinity, duration: 1.5 }}>→</motion.span>
                </a>
              </div>
            </div>

            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#6FB6E8]/20 to-[#1E7FD1]/20 rounded-[2rem] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="relative bg-[#0d1117] rounded-3xl border border-white/10 overflow-hidden shadow-2xl">
                <div className="flex items-center gap-2 px-6 py-4 border-b border-white/5 bg-white/5">
                  <div className="w-3 h-3 rounded-full bg-[#ff5f56]" />
                  <div className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
                  <div className="w-3 h-3 rounded-full bg-[#27c93f]" />
                  <span className="ml-4 text-xs font-mono text-white/30 uppercase tracking-widest">main.py</span>
                </div>
                <CodeReveal
                  language="python"
                  lineDelay={300}
                  charDelay={10}
                  showLineNumbers={true}
                  showCursor={true}
                  beatId="home-sdk-preview"
                  code={`
import ice
import asyncio
import os
# 1. Set your signed license key
os.environ["ICE_LICENSE_JWT"] = "your_signed_jwt_here"
async def main():
    # 2. Ingest documents into memory
    await ice.ingest("s3://data-lake/architecture.pdf")
    # 3. Query with automatic memory retrieval
    response = await ice.query(
        prompt="Analyze this.",
        model="gpt-4o"
    )
    print(response)
asyncio.run(main())
`}
                />
                <NarrativeCaption text="Three lines. That's the whole integration." delay={3000} />
              </div>
            </div>
          </div>
        </Section>

        {/* Other Products Section — The Cast */}
        <section className="bg-[#191d2b] py-32 border-t border-[#FFFFFF]/5" id="the-cast">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <span className="text-[#FFFFFF]/30 font-mono tracking-[0.4em] uppercase text-xs mb-6 block">The Cast</span>
              <h2 className="text-4xl md:text-5xl font-extrabold mb-8 tracking-tight text-[#FFFFFF]">Three products, one way of thinking.</h2>
              <p className="text-[#FFFFFF]/60 text-lg font-light max-w-2xl mx-auto">GoingMerry is the next product we are building after ICE.</p>
            </div>

            <div className={RAMSEY_ENABLED ? "grid md:grid-cols-4 gap-8" : "grid md:grid-cols-3 gap-8"}>
              <ChapterCard
                chapter={1}
                theme="Memory"
                name="ICE"
                color="ice"
                tagline="Indexless Context Engine. Turn-granular memory for AI agents."
                role="Our first product"
                href="/ice"
                index={0}
              />
              <ChapterCard
                chapter={2}
                theme="Velocity"
                name="GoingMerry"
                color="goingmerry"
                tagline="Local AI runtime. Run models privately on your own hardware."
                role="The next product after ICE"
                href="/goingmerry"
                index={1}
              />
              {RAMSEY_ENABLED && (
                <ChapterCard
                  chapter={3}
                  theme="Truth"
                  name="Ramsey"
                  color="ramsey"
                  tagline="Deterministic reasoning engine. Logic without hallucination."
                  role="The judge — formal verification for critical paths"
                  href="/ramsey"
                  index={2}
                />
              )}
              <ChapterCard
                chapter={RAMSEY_ENABLED ? 4 : 3}
                theme="Symphony"
                name="Cortex"
                color="cortex"
                tagline="Multi-agent orchestration runtime. Auditable, not just functional."
                role="Open source, on GitHub"
                href="https://github.com/Dopove/Cortex"
                external={true}
                index={RAMSEY_ENABLED ? 3 : 2}
              />
            </div>
          </div>
        </section>

        <section aria-label="What we are learning" className="border-t border-[#FFFFFF]/5 py-16">
          <p className="max-w-2xl mx-auto px-6 text-center text-base text-[#FFFFFF]/60 font-light leading-relaxed">
            We are experimenting with an autonomous approach to our own marketing, to reduce founder workload. This site reflects what we are learning, including what is still unfinished.
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
}
